// CME (Continuing Medical Education) Category 2 - Session Tracking Types
//
// Schemas are authoritative — the TS types are derived via z.infer<>. Consumers
// can import the *Schema values for runtime validation, or the matching types for
// compile-time typing (unchanged from the pre-migration API).

import { z } from 'zod';

// --- Session Status State Machine ---
// active → paused (5 min idle OR app backgrounded OR manual)
// paused → active (user resumes within 1 hour)
// paused → discontinued (1 hour expiry, server-side scheduled check)
// active → completed (user ends session + attestation)
// active|paused → cancelled (user discards)
export const CmeSessionStatusSchema = z.enum([
  'active',
  'paused',
  'completed',
  'discontinued',
  'cancelled',
]);
export type CmeSessionStatus = z.infer<typeof CmeSessionStatusSchema>;

// --- Activity Types ---
export const CmeActivityTypeSchema = z.enum(['question', 'quiz', 'discussion']);
export type CmeActivityType = z.infer<typeof CmeActivityTypeSchema>;

export const CmeActivitySchema = z.object({
  type: CmeActivityTypeSchema,
  itemId: z.string(),
  subject: z.string(),
  specialty: z.string().optional(),
  timestamp: z.string(), // ISO UTC string (client device time, validated by server)
});
export type CmeActivity = z.infer<typeof CmeActivitySchema>;

// --- Session Summary ---
export const CmeSubjectBreakdownSchema = z.object({
  name: z.string(),
  count: z.number(),
  minutes: z.number(),
});
export type CmeSubjectBreakdown = z.infer<typeof CmeSubjectBreakdownSchema>;

export const CmeSessionSummarySchema = z.object({
  totalActiveMinutes: z.number(),
  subjects: z.array(CmeSubjectBreakdownSchema),
  questionsAnswered: z.number(),
  questionsCorrect: z.number(),
  uniqueQuestions: z.number(),
  duplicateCount: z.number(),
});
export type CmeSessionSummary = z.infer<typeof CmeSessionSummarySchema>;

// --- Session Document (Firestore) ---
export const CmeSessionDocumentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  startedAt: z.string(), // ISO UTC
  endedAt: z.string().optional(), // ISO UTC
  pausedAt: z.string().nullable().optional(), // ISO UTC, null if not paused
  // Closed pause intervals, appended on resume. Backend-only: paused time is excluded from the
  // credited minutes at end-session, whatever the client's stopwatch says.
  pauses: z.array(z.object({ pausedAt: z.string(), resumedAt: z.string() })).optional(),
  status: CmeSessionStatusSchema,
  activities: z.array(CmeActivitySchema),
  summary: CmeSessionSummarySchema.optional(),
  title: z.string().optional(), // Auto-generated
  attestation: z.string().optional(), // User's "What did you learn?" text
  claimedHours: z.number().optional(), // Decimal, FLOORED to 0.25
  // How claimedHours was arrived at (backend-only, for support): the client's stopwatch, the
  // server-observed activity minutes that cap it, and the paused minutes excluded from both.
  creditBasis: z
    .object({
      clientMinutes: z.number().nullable(),
      observedMinutes: z.number(),
      pausedMinutes: z.number(),
    })
    .optional(),
  createdAt: z.string(), // ISO UTC
  updatedAt: z.string(), // ISO UTC
});
export type CmeSessionDocument = z.infer<typeof CmeSessionDocumentSchema>;

// --- Session View Data (API Response) ---
export const CmeSessionViewDataSchema = z.object({
  id: z.string(),
  userId: z.string(),
  startedAt: z.string(),
  endedAt: z.string().optional(),
  pausedAt: z.string().nullable().optional(),
  status: CmeSessionStatusSchema,
  activityCount: z.number(),
  summary: CmeSessionSummarySchema.optional(),
  title: z.string().optional(),
  attestation: z.string().optional(),
  claimedHours: z.number().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CmeSessionViewData = z.infer<typeof CmeSessionViewDataSchema>;

// --- Incorrect Question (for Learning Recap) ---
export const CmeIncorrectQuestionSchema = z.object({
  questionId: z.string(),
  questionText: z.string(),
  subject: z.string(),
});
export type CmeIncorrectQuestion = z.infer<typeof CmeIncorrectQuestionSchema>;

// --- Session Detail (includes activities for detail screen) ---
export const CmeSessionDetailViewDataSchema = CmeSessionViewDataSchema.extend({
  activities: z.array(CmeActivitySchema),
  incorrectQuestions: z.array(CmeIncorrectQuestionSchema).optional(),
});
export type CmeSessionDetailViewData = z.infer<typeof CmeSessionDetailViewDataSchema>;

// --- Certificate ---
export const CmeCertificateStatusSchema = z.enum(['generating', 'ready', 'failed']);
export type CmeCertificateStatus = z.infer<typeof CmeCertificateStatusSchema>;

export const CmeCertificateDocumentSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  userId: z.string(),
  generatedAt: z.string().optional(), // ISO UTC
  pdfStoragePath: z.string().optional(),
  downloadUrl: z.string().optional(),
  title: z.string(),
  hours: z.number(),
  status: CmeCertificateStatusSchema,
  certificateUuid: z.string(), // Unique Certificate ID for verification
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CmeCertificateDocument = z.infer<typeof CmeCertificateDocumentSchema>;

export const CmeCertificateViewDataSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  title: z.string(),
  hours: z.number(),
  status: CmeCertificateStatusSchema,
  downloadUrl: z.string().optional(),
  certificateUuid: z.string(),
  generatedAt: z.string().optional(),
  createdAt: z.string(),
});
export type CmeCertificateViewData = z.infer<typeof CmeCertificateViewDataSchema>;

// --- State Requirements ---
export const CmeStateRequirementSchema = z.object({
  state: z.string(),
  renewalCycle: z.string(),
  totalHoursRequired: z.string(),
  minCategory1: z.string(),
  maxCategory2: z.string(),
  notes: z.string().optional(),
});
export type CmeStateRequirement = z.infer<typeof CmeStateRequirementSchema>;

// --- API Request/Response Types ---

export const CmeStartSessionResponseSchema = z.object({
  sessionId: z.string(),
  startedAt: z.string(),
});
export type CmeStartSessionResponse = z.infer<typeof CmeStartSessionResponseSchema>;

export const CmeLogActivityBodySchema = z.object({
  activities: z.array(CmeActivitySchema),
});
export type CmeLogActivityBody = z.infer<typeof CmeLogActivityBodySchema>;

export const CmeEndSessionBodySchema = z.object({
  attestation: z.string(),
  timezone: z.string().optional(), // IANA timezone for certificate display
});
export type CmeEndSessionBody = z.infer<typeof CmeEndSessionBodySchema>;

export const CmeEndSessionResponseSchema = z.object({
  session: CmeSessionViewDataSchema,
});
export type CmeEndSessionResponse = z.infer<typeof CmeEndSessionResponseSchema>;

export const CmeGenerateCertificateResponseSchema = z.object({
  certificateId: z.string(),
  status: CmeCertificateStatusSchema,
});
export type CmeGenerateCertificateResponse = z.infer<typeof CmeGenerateCertificateResponseSchema>;

export const CmeSessionListResponseSchema = z.object({
  data: z.array(CmeSessionViewDataSchema),
  hasMore: z.boolean(),
  lastDocId: z.string().optional(),
});
export type CmeSessionListResponse = z.infer<typeof CmeSessionListResponseSchema>;

export const CmeCertificateListResponseSchema = z.object({
  data: z.array(CmeCertificateViewDataSchema),
});
export type CmeCertificateListResponse = z.infer<typeof CmeCertificateListResponseSchema>;

export const CmeStateRequirementsResponseSchema = z.object({
  data: z.array(CmeStateRequirementSchema),
});
export type CmeStateRequirementsResponse = z.infer<typeof CmeStateRequirementsResponseSchema>;

// --- CME Requirement Search Types ---

export const ProfessionSchema = z.enum(['MD', 'DO', 'PA', 'NP', 'DMD']);
export type Profession = z.infer<typeof ProfessionSchema>;

export const CmeCategorySchema = z.enum(['specialty', 'certification', 'state']);
export type CmeCategory = z.infer<typeof CmeCategorySchema>;

export const CmeRequirementSchema = z.object({
  profession: ProfessionSchema,
  category: CmeCategorySchema,
  name: z.string(),
  cycle: z.string(),
  totalRequired: z.string(),
  accreditedRequired: z.string(),
  allowableClass2Hours: z.string(),
});
export type CmeRequirement = z.infer<typeof CmeRequirementSchema>;

export const CmeDataSchema = z.object({
  professionRequirements: z.array(CmeRequirementSchema),
  stateRequirements: z.array(CmeStateRequirementSchema),
});
export type CmeData = z.infer<typeof CmeDataSchema>;

export const CmeSearchParamsSchema = z.object({
  profession: ProfessionSchema.optional(),
  category: CmeCategorySchema.optional(),
  state: z.string().optional(),
  searchTerm: z.string().optional(),
});
export type CmeSearchParams = z.infer<typeof CmeSearchParamsSchema>;

export const CmeSearchResponseSchema = z.object({
  professionRequirements: z.array(CmeRequirementSchema),
  stateRequirements: z.array(CmeStateRequirementSchema),
});
export type CmeSearchResponse = z.infer<typeof CmeSearchResponseSchema>;
